/**
 * WidgetConfig — the public, browser-safe configuration returned by
 * `GET /v1/config/:publicKey`. Contains NO secret material (the secret key is
 * never shipped). Sub-shapes (masking / features / agent / branding) mirror the
 * JSON columns on `WidgetInstance`.
 */
import type { z } from 'zod';
import { array, boolean, enum as zEnum, iso, number, object, string, url } from 'zod';

import { AgentProviderSchema, CaptureLevelSchema, LocaleSchema } from './enums.js';

/** Client-side PII masking config (first redaction layer, before anything leaves the browser). */
export const MaskingConfigSchema = object({
  /** Mask all <input>/<textarea> values by default. */
  maskInputs: boolean().default(true),
  /** CSS selectors whose text/values are masked. */
  maskSelectors: array(string().max(512)).max(200).default([]),
  /** CSS selectors removed entirely from screenshots/replay. */
  blockSelectors: array(string().max(512)).max(200).default([]),
  /** Regex source strings scrubbed from captured text (email/card/phone, …). */
  scrubPatterns: array(string().max(512)).max(100).default([]),
});
export type MaskingConfig = z.infer<typeof MaskingConfigSchema>;

/** Feature toggles per widget instance. */
export const FeatureFlagsSchema = object({
  voiceAgent: boolean().default(false),
  textAgent: boolean().default(false),
  replay: boolean().default(false),
  implicitFiling: boolean().default(false),
});
export type FeatureFlags = z.infer<typeof FeatureFlagsSchema>;

/**
 * Live-agent configuration. `systemPromptOverride` and model/voice are
 * config-driven; model ids stay blank locally (Fake provider) and are verified
 * at wire-time for real providers.
 */
export const AgentConfigSchema = object({
  provider: AgentProviderSchema.default('fake'),
  model: string().max(256).default(''),
  voice: string().max(64).optional(),
  systemPromptOverride: string().max(8192).optional(),
  knowledgeEnabled: boolean().default(false),
  /** Hard cap on a single live session, milliseconds. */
  maxSessionMs: number().int().positive().default(300_000),
});
export type AgentConfig = z.infer<typeof AgentConfigSchema>;

/** Launcher appearance — how the widget's entry point looks / is opened. */
export const WidgetLauncherSchema = zEnum([
  /** Small circular icon button (the default). */
  'bubble',
  /** Labelled pill button (icon + `launcherText`) — form-forward. */
  'bar',
  /** Tiny text anchor pinned to the edge — low-profile. */
  'anchor',
  /** Slide-out handle/tab affixed to the screen edge. */
  'tab',
  /** No visible launcher; opened only via the keyboard `hotkey`. */
  'hidden',
]);
export type WidgetLauncher = z.infer<typeof WidgetLauncherSchema>;

/** Overall widget scale. */
export const WidgetSizeSchema = zEnum(['small', 'medium', 'large']);
export type WidgetSize = z.infer<typeof WidgetSizeSchema>;

/** Branding tokens for the Shadow-DOM widget UI. */
export const BrandingConfigSchema = object({
  primaryColor: string().max(32).optional(),
  accentColor: string().max(32).optional(),
  logoUrl: url().max(2048).optional(),
  /** Launcher / open-affordance appearance. */
  launcher: WidgetLauncherSchema.default('bubble'),
  /** Overall widget scale (launcher + panel). */
  size: WidgetSizeSchema.default('medium'),
  /** Floating-launcher starting corner. */
  position: zEnum(['bottom-right', 'bottom-left', 'top-right', 'top-left'])
    .default('bottom-right'),
  /** Let the visitor drag the launcher to reposition it (persisted in their browser). */
  draggable: boolean().default(false),
  /**
   * Keyboard shortcut that toggles the widget, e.g. `"mod+shift+f"` (`mod` = ⌘ on
   * macOS, Ctrl elsewhere). Required to open the `hidden` launcher; an optional
   * accelerator for the others.
   */
  hotkey: string().max(64).optional(),
  /** Launcher button label. */
  launcherText: string().max(64).optional(),
  /** Light/dark/auto theme hint. */
  theme: zEnum(['light', 'dark', 'auto']).default('auto'),
  /** Default widget UI language; the browser can still auto-detect / the user override. */
  defaultLanguage: LocaleSchema.optional(),
});
export type BrandingConfig = z.infer<typeof BrandingConfigSchema>;

/**
 * Full public config payload. NOTE: agent.systemPromptOverride is intentionally
 * NOT exposed here — it is server-side only. The browser only needs provider +
 * model + voice to drive the UI; the proxy injects the prompt.
 */
export const AgentPublicConfigSchema = AgentConfigSchema.omit({
  systemPromptOverride: true,
});
export type AgentPublicConfig = z.infer<typeof AgentPublicConfigSchema>;

export const WidgetConfigSchema = object({
  /** Public key this config was resolved from (echoed for the widget). */
  publicKey: string().min(1),
  /** Capture level 0–3. */
  captureLevel: CaptureLevelSchema,
  features: FeatureFlagsSchema,
  masking: MaskingConfigSchema,
  agent: AgentPublicConfigSchema,
  branding: BrandingConfigSchema,
  /** ISO-8601 timestamp the config was generated (drives client caching). */
  updatedAt: iso.datetime(),
});
export type WidgetConfig = z.infer<typeof WidgetConfigSchema>;
